import { motion } from 'framer-motion';
import { Github, Instagram, Mail, MapPin, Award, PenTool as Tool, Code, FileText, Camera, Heart } from 'lucide-react';

interface TeamMember {
  name: string;
  role: string;
  description: string;
  image: string;
  icon: JSX.Element;
  links?: {
    instagram?: string;
    email?: string;
    github?: string;
  };
}

const teamMembers: TeamMember[] = [
  {
    name: "Micu Bogdan",
    role: "Entrepreneur & Environmental Activist",
    description: "A visionary entrepreneur with a passion for environmental conservation and a decorated climbing athlete. Leading NHVelo's strategic initiatives while promoting sustainable mountain biking practices.",
    image: "https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-25%20at%2009.55.27-min.png?alt=media&token=2ca49f3c-fed8-40e4-9611-cf7be86e9630",
    icon: <Heart className="w-6 h-6 text-red-500" />,
    links: {
      instagram: "#",
      email: "mailto:contact@nhvelo.ro"
    }
  },
  {
    name: "Sebastian Marian",
    role: "Club Manager & Veteran Athlete",
    description: "With over 30 years of competition experience, Sebastian brings unparalleled expertise in sports management and athlete development to NHVelo.",
    image: "https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-25%20at%2009.57.37-min.png?alt=media&token=3f0a85b1-816b-4341-b4a8-ced9f3e3b413",
    icon: <Award className="w-6 h-6 text-blue-500" />,
    links: {
      instagram: "#",
      email: "mailto:sebastian@nhvelo.ro"
    }
  },
  {
    name: "Ion Ciprian Ovidiu",
    role: "Trail Builder & Sports Enthusiast",
    description: "The mastermind behind our world-class trails, Ciprian combines technical expertise with a deep passion for mountain biking to create unforgettable riding experiences.",
    image: "https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-25%20at%2009.58.28-min.png?alt=media&token=444f84e0-773c-4975-9685-4cabd0e24152",
    icon: <Tool className="w-6 h-6 text-green-500" />,
    links: {
      instagram: "#",
      email: "mailto:ciprian@nhvelo.ro"
    }
  },
  {
    name: "Ion Valentina",
    role: "Documentation Specialist & MTB Rider",
    description: "The organizational backbone of NHVelo, Valentina ensures smooth operations while actively participating in the mountain biking community.",
    image: "https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-25%20at%2009.56.13-min.png?alt=media&token=8ad856fc-401a-41c3-8793-cb85f549b83b",
    icon: <FileText className="w-6 h-6 text-purple-500" />,
    links: {
      email: "mailto:valentina@nhvelo.ro"
    }
  },
  {
    name: "Sebi POW",
    role: "Pro Athlete & Social Media Manager",
    description: "A medal-winning athlete with a keen eye for content creation, Sebi brings our community together through engaging social media presence.",
    image: "https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-01-03%20at%2010.32.04.png?alt=media&token=71ab1a28-bf62-4a51-a61c-5495c87b67ee",
    icon: <Camera className="w-6 h-6 text-yellow-500" />,
    links: {
      instagram: "#"
    }
  },
  {
    name: "Sacuiu Razvan",
    role: "Web Developer & Former Athlete",
    description: "Combining technical expertise with athletic experience, Razvan bridges the digital and physical aspects of NHVelo's presence.",
    image: "https://firebasestorage.googleapis.com/v0/b/nhvelo-f0124.appspot.com/o/NhVeloSite%2FScreenshot%202025-04-25%20at%2009.59.03-min.png?alt=media&token=656ecfd6-68c7-46e1-8602-383fb2a56f92",
    icon: <Code className="w-6 h-6 text-indigo-500" />,
    links: {
      github: "#",
      email: "mailto:razvan@nhvelo.ro"
    }
  }
];

export function CoreTeamSection() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold mb-4">Our Core Team</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Meet the passionate individuals driving NHVelo's mission to elevate mountain biking in Romania
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-all duration-300"
            >
              <div className="relative h-64">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-md">
                  {member.icon}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                <p className="text-orange-500 font-semibold mb-4">{member.role}</p>
                <p className="text-gray-600 mb-6">{member.description}</p>
                <div className="flex items-center gap-4">
                  {member.links?.instagram && (
                    <a href={member.links.instagram} className="text-gray-600 hover:text-orange-500 transition-colors">
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {member.links?.email && (
                    <a href={member.links.email} className="text-gray-600 hover:text-orange-500 transition-colors">
                      <Mail className="w-5 h-5" />
                    </a>
                  )}
                  {member.links?.github && (
                    <a href={member.links.github} className="text-gray-600 hover:text-orange-500 transition-colors">
                      <Github className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}